import {
  DEFAULT_WIDGET_CONFIG,
  VALIDATION_MESSAGES,
  MODEL_FAMILIES,
} from "../constants";
import { isValidHexColor, isValidDomain, isValidUrl } from "../formatters";

// Single source of truth for numeric field bounds used across both validation layers.
// validateDraftData (security) and validateStep (UX) both look up from here, so
// adding a new numeric field's range requires one edit instead of two.
export const NUMERIC_FIELD_BOUNDS = {
  temperature: {
    min: DEFAULT_WIDGET_CONFIG.TEMPERATURE_MIN,
    max: DEFAULT_WIDGET_CONFIG.TEMPERATURE_MAX,
    message: VALIDATION_MESSAGES.TEMPERATURE_RANGE,
  },
  max_tokens: {
    min: DEFAULT_WIDGET_CONFIG.MAX_TOKENS_MIN,
    max: DEFAULT_WIDGET_CONFIG.MAX_TOKENS_MAX,
    message: VALIDATION_MESSAGES.MAX_TOKENS_RANGE,
  },
  rate_limit_per_ip: {
    min: DEFAULT_WIDGET_CONFIG.RATE_LIMIT_PER_IP_MIN,
    max: DEFAULT_WIDGET_CONFIG.RATE_LIMIT_PER_IP_MAX,
    message: VALIDATION_MESSAGES.RATE_LIMIT_RANGE,
  },
  conversation_ttl_hours: {
    min: DEFAULT_WIDGET_CONFIG.CONVERSATION_TTL_MIN,
    max: DEFAULT_WIDGET_CONFIG.CONVERSATION_TTL_MAX,
    message: VALIDATION_MESSAGES.CONVERSATION_TTL_RANGE,
  },
};

const DRAFT_STRING_FIELDS = [
  "name",
  "display_name",
  "status",
  "primary_color",
  "secondary_color",
  "avatar_url",
  "welcome_message",
  "placeholder_text",
  "branding_text",
  "branding_url",
  "system_prompt",
  "model",
  "allowed_domains",
];

// max_tokens and conversation_ttl_hours use wider draft-lenient ceilings to avoid
// rejecting drafts saved under older or more permissive configurations; the UX
// validator (validateStep) enforces the tighter form limits on submission.
const DRAFT_NUMERIC_FIELDS = [
  {
    name: "temperature",
    min: NUMERIC_FIELD_BOUNDS.temperature.min,
    max: NUMERIC_FIELD_BOUNDS.temperature.max,
  },
  { name: "max_tokens", min: NUMERIC_FIELD_BOUNDS.max_tokens.min, max: 100000 },
  {
    name: "rate_limit_per_ip",
    min: NUMERIC_FIELD_BOUNDS.rate_limit_per_ip.min,
    max: NUMERIC_FIELD_BOUNDS.rate_limit_per_ip.max,
  },
  {
    name: "conversation_ttl_hours",
    min: NUMERIC_FIELD_BOUNDS.conversation_ttl_hours.min,
    max: 8760,
  },
];

function validateDraftStringFields(data) {
  for (const field of DRAFT_STRING_FIELDS) {
    if (data[field] !== undefined && typeof data[field] !== "string")
      return false;
  }
  return true;
}

function validateDraftNumericFields(data) {
  for (const field of DRAFT_NUMERIC_FIELDS) {
    if (data[field.name] !== undefined) {
      if (typeof data[field.name] !== "number") return false;
      if (data[field.name] < field.min || data[field.name] > field.max)
        return false;
    }
  }
  return true;
}

// Check credit_exhaustion_behavior JSONB field.
// Partial objects (e.g. { haiku: "block" }) are intentionally allowed here:
// drafts may be saved mid-edit. The backend buildWidgetValidation() enforces
// completeness on submission; WidgetForm fills missing keys from defaults on load.
function validateDraftCreditExhaustionBehavior(data) {
  if (data.credit_exhaustion_behavior === undefined) return true;
  if (
    typeof data.credit_exhaustion_behavior !== "object" ||
    data.credit_exhaustion_behavior === null
  )
    return false;
  const validKeys = new Set(MODEL_FAMILIES.map((f) => f.key));
  const validValues = new Set(["block", "downgrade"]);
  for (const [key, value] of Object.entries(data.credit_exhaustion_behavior)) {
    if (!validKeys.has(key)) return false;
    if (!validValues.has(value)) return false;
  }
  return true;
}

/**
 * Validate draft data structure to prevent malicious localStorage injection.
 * Checks field types, numeric bounds, color formats, and URL safety.
 * Does NOT mutate the input object.
 */
export const validateDraftData = (data) => {
  if (!data || typeof data !== "object") return false;
  if (!validateDraftStringFields(data)) return false;
  if (!validateDraftNumericFields(data)) return false;
  if (
    data.persist_conversations !== undefined &&
    typeof data.persist_conversations !== "boolean"
  )
    return false;
  if (!validateDraftCreditExhaustionBehavior(data)) return false;
  if (data.primary_color && !isValidHexColor(data.primary_color)) return false;
  if (data.secondary_color && !isValidHexColor(data.secondary_color))
    return false;
  if (data.avatar_url?.trim() && !isValidUrl(data.avatar_url)) return false;
  if (data.branding_url?.trim() && !isValidUrl(data.branding_url)) return false;
  return true;
};

function validateStep1(formData) {
  const errors = {};
  if (!formData.name.trim()) {
    errors.name = VALIDATION_MESSAGES.WIDGET_NAME_REQUIRED;
  } else if (formData.name.length > DEFAULT_WIDGET_CONFIG.NAME_MAX_LENGTH) {
    errors.name = VALIDATION_MESSAGES.WIDGET_NAME_TOO_LONG;
  }
  if (!formData.display_name.trim()) {
    errors.display_name = VALIDATION_MESSAGES.DISPLAY_NAME_REQUIRED;
  } else if (
    formData.display_name.length > DEFAULT_WIDGET_CONFIG.DISPLAY_NAME_MAX_LENGTH
  ) {
    errors.display_name = VALIDATION_MESSAGES.DISPLAY_NAME_TOO_LONG;
  }
  return errors;
}

function validateStep2(formData) {
  const errors = {};
  if (!isValidHexColor(formData.primary_color)) {
    errors.primary_color = VALIDATION_MESSAGES.COLOR_INVALID;
  }
  if (!isValidHexColor(formData.secondary_color)) {
    errors.secondary_color = VALIDATION_MESSAGES.COLOR_INVALID;
  }
  if (!formData.welcome_message.trim()) {
    errors.welcome_message = VALIDATION_MESSAGES.WELCOME_MESSAGE_REQUIRED;
  }
  if (!formData.placeholder_text.trim()) {
    errors.placeholder_text = VALIDATION_MESSAGES.PLACEHOLDER_REQUIRED;
  }
  if (formData.avatar_url && !isValidUrl(formData.avatar_url)) {
    errors.avatar_url = VALIDATION_MESSAGES.URL_FORMAT_INVALID;
  }
  if (formData.branding_url && !isValidUrl(formData.branding_url)) {
    errors.branding_url = VALIDATION_MESSAGES.URL_FORMAT_INVALID;
  }
  return errors;
}

function validateStep3(formData) {
  const errors = {};
  if (!formData.system_prompt.trim()) {
    errors.system_prompt = VALIDATION_MESSAGES.SYSTEM_PROMPT_REQUIRED;
  } else if (
    formData.system_prompt.trim().length <
    DEFAULT_WIDGET_CONFIG.SYSTEM_PROMPT_MIN_LENGTH
  ) {
    errors.system_prompt = VALIDATION_MESSAGES.SYSTEM_PROMPT_TOO_SHORT;
  }
  for (const field of ["temperature", "max_tokens"]) {
    const { min, max, message } = NUMERIC_FIELD_BOUNDS[field];
    if (formData[field] < min || formData[field] > max) {
      errors[field] = message;
    }
  }
  return errors;
}

function validateStep4(formData) {
  const errors = {};
  const rateLimitBounds = NUMERIC_FIELD_BOUNDS.rate_limit_per_ip;
  if (
    formData.rate_limit_per_ip < rateLimitBounds.min ||
    formData.rate_limit_per_ip > rateLimitBounds.max
  ) {
    errors.rate_limit_per_ip = rateLimitBounds.message;
  }
  if (formData.persist_conversations) {
    const ttl = NUMERIC_FIELD_BOUNDS.conversation_ttl_hours;
    if (
      formData.conversation_ttl_hours < ttl.min ||
      formData.conversation_ttl_hours > ttl.max
    ) {
      errors.conversation_ttl_hours = ttl.message;
    }
  }
  if (formData.allowed_domains.trim()) {
    const domains = formData.allowed_domains
      .split("\n")
      .map((d) => d.trim())
      .filter(Boolean);
    const invalidDomains = domains.filter((domain) => !isValidDomain(domain));
    if (invalidDomains.length > 0) {
      errors.allowed_domains = `${VALIDATION_MESSAGES.INVALID_DOMAINS} ${invalidDomains.slice(0, 3).join(", ")}${invalidDomains.length > 3 ? "..." : ""}`;
    }
  }
  return errors;
}

/**
 * Validate a specific step of the widget form
 */
export const validateStep = (step, formData) => {
  switch (step) {
    case 1:
      return validateStep1(formData);
    case 2:
      return validateStep2(formData);
    case 3:
      return validateStep3(formData);
    case 4:
      return validateStep4(formData);
    default:
      return {};
  }
};
